import Link from 'next/link'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Text } from '@machobear/design-system'

import { breakpoints, colors } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import defaultBuilding from '@/features/ListingDetail/images/default-building.png'
import CdnImage from '@/components/cdn-image'
import { Label, ParagraphRegular } from '@/components/text'

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: auto;
  min-height: 272px;
  border-radius: 8px;
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
  }
`

const ImageWrapper = styled.div`
  background-color: ${colors.background};
  height: auto;
  width: 272px;
  border-radius: 8px 0 0 8px;
  overflow: hidden;
  position: relative;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex: initial;
    height: 220px;
    width: 100%;
    border-radius: 8px 8px 0 0;
  }
`

const ImageLink = styled.a``

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  padding: 20px 24px;
  background: ${colors.background};
  gap: 20px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    height: auto;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled(ParagraphRegular)`
  color: ${colors.black};
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 16px;
    line-height: 20px;
  }
`

const Location = styled(Label)`
  color: ${colors.grey32};
  font-weight: 400;
`

const Items = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
`

const Item = styled(ParagraphRegular)`
  color: ${colors.grey24};
  font-weight: 500;
  width: 175px;
`

const BoldItem = styled(ParagraphRegular)`
  color: ${colors.black};
  font-weight: 700;
`

const BuildingLink = styled.a`
  color: ${colors.link};
  text-decoration: none;
  width: fit-content;

  > div {
    font-weight: 500;
    pointer-events: none;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 14px;
    line-height: 20px;
  }
`

const getCover = images => {
  if (!images?.length) return defaultBuilding
  const cover = images.find(({ tag }) => tag === 'cover_photo')
  return cover?.aws_s3_key ?? images[0].aws_s3_key
}

const BuildingInfo = ({
  data,
  buildingImages,
  buildingUrl,
  isListingLocked
}) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const buildingImage = getCover(buildingImages)
  const buildingName = data?.name

  return (
    <Wrapper>
      <ImageWrapper>
        {isListingLocked ? (
          <CdnImage
            src={buildingImage}
            alt={`${data.name} Apartment Building`}
            sizes={displayMobile ? '100vw' : '100%'}
            layout="fill"
            objectFit="cover"
            objectPosition="bottom"
            placeholder="blur"
          />
        ) : (
          <Link href={buildingUrl} passHref legacyBehavior>
            <ImageLink>
              <CdnImage
                src={buildingImage}
                alt={`${data.name} Apartment Building`}
                sizes={displayMobile ? '100vw' : '100%'}
                layout="fill"
                objectFit="cover"
                objectPosition="bottom"
                placeholder="blur"
              />
            </ImageLink>
          </Link>
        )}
      </ImageWrapper>
      <InfoWrapper>
        <TitleWrapper>
          <Title>{buildingName}</Title>
          <Location fontSize="14px">
            {data.neighborhood || data.full_street_name}
          </Location>
        </TitleWrapper>
        <Items>
          <Row>
            <Item>
              {t(
                'listing:b.building.view.year_completed.text',
                'Year Completed'
              )}
            </Item>
            <BoldItem>{data?.year_built || '-'}</BoldItem>
          </Row>
          <Row>
            <Item>{t('listing:b.building.view.floors.text', 'Floors')}</Item>
            <BoldItem>{data?.count_stories || '-'}</BoldItem>
          </Row>
          <Row>
            <Item>{t('listing:b.building.view.units.text', 'Units')}</Item>
            <BoldItem>{data?.count_units || '-'}</BoldItem>
          </Row>
        </Items>
        {isListingLocked ? (
          <Text tone="disabled" size="medium" weight="medium">
            <Trans i18nKey="listing:b.listing.view.building_learn_more.text">
              Learn more about {{ buildingName }}
            </Trans>
          </Text>
        ) : (
          <Link href={buildingUrl} passHref legacyBehavior>
            <BuildingLink id="listing-details-learnmore-building-details-click">
              <ParagraphRegular>
                <Trans i18nKey="listing:b.listing.view.building_learn_more.text">
                  Learn more about {{ buildingName }}
                </Trans>
              </ParagraphRegular>
            </BuildingLink>
          </Link>
        )}
      </InfoWrapper>
    </Wrapper>
  )
}

export default BuildingInfo
